/**
 * Main class for behavior detection
 * Uses modular strategy pattern for tree-shakeable, autonomous detection modules
 */
export class BehaviorDetector {
    constructor(tickOptions) {
        var _a, _b, _c, _d;
        // Strategy mode
        this.strategies = new Map();
        this.isTracking = false;
        this.isPausedByVisibility = false;
        this.tickInterval = null;
        this.tickIntervalMs = 1000; // Default: 1 second
        this.pauseOnHidden = true;
        this.visibilityChangeHandler = null;
        // Confidence tracking
        this.confidenceScore = 0;
        this.CONFIDENCE_TARGET = 1.0; // Target confidence for reliable classification
        this.CONFIDENCE_DECAY = 0.95; // Per event decay to prevent infinite growth
        this.scoreFloor = 1.0;
        this.lastEventTime = 0;
        if (tickOptions === null || tickOptions === void 0 ? void 0 : tickOptions.interval) {
            this.tickIntervalMs = tickOptions.interval;
        }
        if ((tickOptions === null || tickOptions === void 0 ? void 0 : tickOptions.pauseOnHidden) !== undefined) {
            this.pauseOnHidden = tickOptions.pauseOnHidden;
        }
        // Score memory defaults
        const mem = tickOptions === null || tickOptions === void 0 ? void 0 : tickOptions.scoreMemory;
        this.scoreMemory = {
            enabled: (_a = mem === null || mem === void 0 ? void 0 : mem.enabled) !== null && _a !== void 0 ? _a : true,
            inactivityThreshold: (_b = mem === null || mem === void 0 ? void 0 : mem.inactivityThreshold) !== null && _b !== void 0 ? _b : 2000,
            maxRecovery: (_c = mem === null || mem === void 0 ? void 0 : mem.maxRecovery) !== null && _c !== void 0 ? _c : 0.1,
            recoveryPerEvent: (_d = mem === null || mem === void 0 ? void 0 : mem.recoveryPerEvent) !== null && _d !== void 0 ? _d : 0.02,
        };
    }
    /**
     * Add a detection strategy
     */
    addStrategy(strategy, weight) {
        const config = {
            strategy,
            weight: weight !== null && weight !== void 0 ? weight : strategy.defaultWeight,
            enabled: true,
        };
        this.strategies.set(strategy.name, config);
        // Set up event callback for confidence tracking
        if (strategy.setEventCallback) {
            strategy.setEventCallback((event) => {
                this.onStrategyEvent(event, config.weight);
            });
        }
        // If already tracking, start this strategy immediately
        if (this.isTracking) {
            strategy.start();
        }
        return this;
    }
    /**
     * Handle event from strategy - update confidence
     */
    onStrategyEvent(event, strategyWeight) {
        // Apply decay to existing confidence to prevent infinite growth
        this.confidenceScore *= this.CONFIDENCE_DECAY;
        // Add weighted contribution from this event
        // eventWeight (0-1) * strategyWeight (e.g. 0.3 for mouse) = contribution
        const contribution = event.weight * strategyWeight;
        this.confidenceScore = Math.min(this.CONFIDENCE_TARGET, this.confidenceScore + contribution);
        // Score memory: track activity and slowly raise the floor
        this.lastEventTime = Date.now();
        if (this.scoreMemory.enabled) {
            this.scoreFloor = Math.min(1.0, this.scoreFloor + this.scoreMemory.recoveryPerEvent);
        }
    }
    /**
     * Remove a detection strategy
     */
    removeStrategy(name) {
        const config = this.strategies.get(name);
        if (config) {
            if (this.isTracking) {
                config.strategy.stop();
            }
            this.strategies.delete(name);
        }
        return this;
    }
    /**
     * Enable/disable a strategy
     */
    setStrategyEnabled(name, enabled) {
        const config = this.strategies.get(name);
        if (config) {
            config.enabled = enabled;
            // If tracking and being disabled, stop it
            if (!enabled && this.isTracking) {
                config.strategy.stop();
            }
            // If tracking and being enabled, start it
            if (enabled && this.isTracking) {
                config.strategy.start();
            }
        }
        return this;
    }
    /**
     * Get all registered strategies
     */
    getStrategies() {
        return this.strategies;
    }
    /**
     * Start tracking
     */
    start() {
        if (this.isTracking)
            return;
        this.isTracking = true;
        // Set up visibility change listener
        if (this.pauseOnHidden && typeof document !== 'undefined') {
            this.visibilityChangeHandler = this.handleVisibilityChange.bind(this);
            document.addEventListener('visibilitychange', this.visibilityChangeHandler);
            // Check initial visibility state
            if (document.hidden) {
                this.isPausedByVisibility = true;
                return; // Don't start strategies if already hidden
            }
        }
        // Start all enabled strategies
        for (const [_, config] of this.strategies) {
            if (config.enabled) {
                config.strategy.start();
            }
        }
        // Start tick mechanism
        this.startTick();
    }
    /**
     * Stop tracking
     */
    stop() {
        if (!this.isTracking)
            return;
        this.isTracking = false;
        this.isPausedByVisibility = false;
        // Remove visibility change listener
        if (this.visibilityChangeHandler && typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
            this.visibilityChangeHandler = null;
        }
        // Stop all strategies
        for (const [_, config] of this.strategies) {
            config.strategy.stop();
        }
        // Stop tick
        this.stopTick();
    }
    /**
     * Reset all data
     */
    reset() {
        this.confidenceScore = 0;
        this.scoreFloor = 1.0;
        this.lastEventTime = 0;
        for (const [_, config] of this.strategies) {
            config.strategy.reset();
        }
    }
    /**
     * Calculate human likelihood score
     */
    async score(options = {}) {
        const breakdown = this.calculateStrategyScore();
        let effectiveScore = breakdown.overall;
        // Score memory: cap recovery during inactivity
        if (this.scoreMemory.enabled && this.lastEventTime > 0) {
            // Track the lowest score observed
            if (effectiveScore < this.scoreFloor) {
                this.scoreFloor = effectiveScore;
            }
            // If inactive, prevent the score from recovering freely
            const timeSinceEvent = Date.now() - this.lastEventTime;
            if (timeSinceEvent > this.scoreMemory.inactivityThreshold) {
                const cap = this.scoreFloor + this.scoreMemory.maxRecovery;
                effectiveScore = Math.min(effectiveScore, cap);
            }
        }
        const result = {
            score: effectiveScore,
        };
        if (options.breakdown) {
            result.breakdown = breakdown;
        }
        // Note: auditTrail not available in strategy mode
        // Each strategy manages its own data independently
        return result;
    }
    /**
     * Check if currently tracking
     */
    isActive() {
        return this.isTracking;
    }
    /**
     * Check if currently paused due to tab visibility
     */
    isPaused() {
        return this.isPausedByVisibility;
    }
    /**
     * Get event count from all strategies
     */
    getEventCount() {
        var _a, _b;
        const counts = {};
        for (const [name, config] of this.strategies) {
            const debug = (_b = (_a = config.strategy).getDebugInfo) === null || _b === void 0 ? void 0 : _b.call(_a);
            if ((debug === null || debug === void 0 ? void 0 : debug.eventCount) !== undefined) {
                counts[name] = debug.eventCount;
            }
        }
        return counts;
    }
    /**
     * Get debug info from all strategies
     */
    getStrategyDebugInfo() {
        const debug = {};
        for (const [name, config] of this.strategies) {
            if (config.strategy.getDebugInfo) {
                debug[name] = config.strategy.getDebugInfo();
            }
        }
        return debug;
    }
    /**
     * Get current confidence score (0-1)
     * Represents how much interaction data we've collected
     * Higher confidence = more reliable classification
     */
    getConfidence() {
        return Math.min(1, this.confidenceScore);
    }
    /**
     * Check if confidence is above threshold for reliable classification
     * @param threshold - Minimum confidence (0-1), default 0.3
     */
    hasConfidentData(threshold = 0.3) {
        return this.getConfidence() >= threshold;
    }
    /**
     * Cleanup resources
     */
    destroy() {
        this.stop();
        this.strategies.clear();
    }
    /**
     * Handle visibility change events
     */
    handleVisibilityChange() {
        if (!this.isTracking)
            return;
        if (document.hidden) {
            // Tab became hidden - pause detection
            if (!this.isPausedByVisibility) {
                this.isPausedByVisibility = true;
                // Stop all strategies
                for (const [_, config] of this.strategies) {
                    if (config.enabled) {
                        config.strategy.stop();
                    }
                }
                // Stop tick
                this.stopTick();
            }
        }
        else {
            // Tab became visible - resume detection
            if (this.isPausedByVisibility) {
                this.isPausedByVisibility = false;
                // Restart all enabled strategies
                for (const [_, config] of this.strategies) {
                    if (config.enabled) {
                        config.strategy.start();
                    }
                }
                // Restart tick
                this.startTick();
            }
        }
    }
    /**
     * Start tick mechanism for strategies
     */
    startTick() {
        if (this.tickInterval !== null)
            return;
        this.tickInterval = window.setInterval(() => {
            const now = Date.now();
            for (const [_, config] of this.strategies) {
                if (config.enabled && config.strategy.onTick) {
                    config.strategy.onTick(now);
                }
            }
        }, this.tickIntervalMs);
    }
    /**
     * Stop tick mechanism
     */
    stopTick() {
        if (this.tickInterval !== null) {
            clearInterval(this.tickInterval);
            this.tickInterval = null;
        }
    }
    /**
     * Calculate score using strategies
     */
    calculateStrategyScore() {
        const factors = {};
        const weights = {};
        let totalWeight = 0;
        let weightedSum = 0;
        for (const [name, config] of this.strategies) {
            if (!config.enabled)
                continue;
            const score = config.strategy.score();
            factors[name] = score;
            weights[name] = config.weight;
            if (score !== undefined && score !== null) {
                totalWeight += config.weight;
                weightedSum += score * config.weight;
            }
        }
        const overall = totalWeight > 0 ? weightedSum / totalWeight : 0.5;
        return {
            overall: Math.max(0, Math.min(1, overall)),
            factors,
            weights,
        };
    }
}
